import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BedDouble,
  Car,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  Mail,
  MessageSquareText,
  Package,
  Phone,
  Plane,
  Ship,
  UsersRound,
} from 'lucide-react';
import { MAIN_PHONE_DISPLAY, MAIN_PHONE_TEL } from '../../constants/contact';
import { DEFAULT_PAGE_META } from '../../constants/pageMeta';
import { CALL_NOTE, COMPANY_EMAIL, COMPANY_NAME, SITE_NAME, SUPPORT_HOURS } from '../../constants/site';
import usePageMeta from '../../utils/usePageMeta';
import HeroSearch from '../components/HeroSearch';
import SiteLayout from '../components/SiteLayout';

const img = (file) => `${process.env.PUBLIC_URL}/images/${file}`;

const SERVICES = [
  {
    Icon: Plane,
    title: 'Flights',
    text: 'One-way, round-trip and multi-city itineraries. We compare routes, connection times and fare rules so you can choose with the full picture.',
  },
  {
    Icon: BedDouble,
    title: 'Hotels & stays',
    text: 'City hotels, beach resorts and family suites, matched to your budget and the area you want to be in.',
  },
  {
    Icon: Car,
    title: 'Car rentals',
    text: 'Airport and city pick-ups, with the deposit, mileage and insurance terms explained before you reserve.',
  },
  {
    Icon: Ship,
    title: 'Cruises',
    text: 'Ocean and river sailings, with help choosing the cabin, sailing date and pre- or post-cruise stay.',
  },
  {
    Icon: Package,
    title: 'Vacation packages',
    text: 'Flights, stays and transfers put together as one trip, with a single itinerary to keep track of.',
  },
  {
    Icon: UsersRound,
    title: 'Group & family travel',
    text: 'Weddings, reunions, school and corporate trips, coordinated by one advisor from first quote to return.',
  },
];

const STEPS = [
  {
    Icon: MessageSquareText,
    title: 'Tell us about your trip',
    text: 'Call us or send the quote form below with where you want to go, your dates and who is travelling.',
  },
  {
    Icon: ClipboardList,
    title: 'Get a clear, itemised quote',
    text: 'We look at the options and send you a quote that lists the provider price, taxes and our service fee separately, along with the change and refund rules.',
  },
  {
    Icon: FileCheck2,
    title: 'Approve, then we book',
    text: 'Nothing is booked or charged until you say yes. You then receive your confirmation and the provider’s booking reference.',
  },
];

const PROMISES = [
  'We are an independent travel agency, not an airline or any other travel provider.',
  'Our service fee is quoted to you in writing before you pay, and is never hidden in another price.',
  'The provider’s own fare rules, baggage allowance and refund terms always apply, and we share them with you before booking.',
  'We never charge your card without your clear approval of the final total.',
  'You are always free to book directly with the airline, hotel or cruise line instead.',
];

const DESTINATIONS = [
  { city: 'Paris', country: 'France', image: 'paris.jpg', text: 'Museums, cafés and day trips to Versailles or the Loire Valley.' },
  { city: 'Tokyo', country: 'Japan', image: 'tokyo.jpg', text: 'Neon nights, quiet temples and easy rail links to Kyoto.' },
  { city: 'London', country: 'United Kingdom', image: 'london.jpg', text: 'Theatre, markets and royal parks, with Europe a short hop away.' },
  { city: 'Dubai', country: 'United Arab Emirates', image: 'dubai.jpg', text: 'Desert safaris, beach resorts and winter sunshine.' },
  { city: 'Cancun', country: 'Mexico', image: 'tropical-beach.jpg', text: 'Caribbean beaches, cenotes and Mayan ruins nearby.' },
  { city: 'New York', country: 'United States', image: 'new-york.jpg', text: 'Broadway, food from every corner of the world and endless neighbourhoods.' },
];

const FAQS = [
  {
    q: `Is ${SITE_NAME} an airline?`,
    a: `No. ${SITE_NAME} is an independent travel agency run by ${COMPANY_NAME}. We are not an airline, hotel or cruise line and we are not affiliated with or endorsed by any travel provider. We help you compare and book trips with those providers.`,
  },
  {
    q: 'Do you charge a fee?',
    a: 'A service fee may apply to bookings made with our help. It is shown as its own line in your written quote, together with the provider price and taxes, and you agree to it before anything is charged. See our Service Fees page for details.',
  },
  {
    q: 'Can I change or cancel a booking made through you?',
    a: 'Yes, we can help. Whether a change or refund is possible, and what it costs, is set by the travel provider’s fare rules. We tell you those rules before you book and again when you ask for a change. See our Cancellation & Refund Policy.',
  },
  {
    q: 'Who answers when I call?',
    a: `A ${SITE_NAME} travel advisor employed by ${COMPANY_NAME}. We cannot access an airline’s internal systems on its behalf, and for some requests the provider may need to be contacted directly — we will tell you when that is the case.`,
  },
  {
    q: 'Are the prices on this website real-time?',
    a: 'This website does not display live fares. Prices change constantly, so we quote current prices to you directly, by phone or email, at the time you ask.',
  },
  {
    q: 'How do you protect my information?',
    a: 'We collect only what is needed to quote and book your trip, share it only with the providers involved in your booking and our service partners, and never sell it. Our Privacy Policy explains this in full.',
  },
];

const TRIP_TYPES = ['Flight', 'Hotel', 'Car rental', 'Cruise', 'Vacation package', 'Group trip'];

function QuoteForm() {
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const line = (label, key) => `${label}: ${data.get(key) || '-'}`;
    const body = [
      line('Name', 'name'),
      line('Email', 'email'),
      line('Phone', 'phone'),
      line('Trip type', 'tripType'),
      line('From', 'from'),
      line('To', 'to'),
      line('Departure date', 'depart'),
      line('Return date', 'return'),
      line('Travellers', 'travellers'),
      '',
      line('Notes', 'notes'),
    ].join('\n');
    const subject = `Trip quote request – ${data.get('tripType')} – ${data.get('name')}`;
    window.location.href = `mailto:${COMPANY_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  return (
    <form className="fx-form" onSubmit={handleSubmit}>
      <div className="fx-form__grid">
        <label>
          Full name
          <input name="name" required autoComplete="name" />
        </label>
        <label>
          Email
          <input name="email" type="email" required autoComplete="email" />
        </label>
        <label>
          Phone (optional)
          <input name="phone" type="tel" autoComplete="tel" />
        </label>
        <label>
          What do you need?
          <select name="tripType" defaultValue="Flight">
            {TRIP_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
        <label>
          Leaving from
          <input name="from" placeholder="City or airport" />
        </label>
        <label>
          Going to
          <input name="to" placeholder="City, region or ship" required />
        </label>
        <label>
          Departure date
          <input name="depart" type="date" />
        </label>
        <label>
          Return date
          <input name="return" type="date" />
        </label>
        <label>
          Travellers
          <input name="travellers" type="number" min="1" max="99" defaultValue="1" />
        </label>
        <label className="fx-form__wide">
          Anything else we should know?
          <textarea name="notes" rows="3" placeholder="Budget, cabin class, hotel area, flexible dates…" />
        </label>
      </div>
      <label className="fx-form__consent">
        <input type="checkbox" required />
        <span>
          I agree that {COMPANY_NAME} may use these details to prepare my quote, as described in the{' '}
          <Link to="/privacy-policy">Privacy Policy</Link> and <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
        </span>
      </label>
      <button type="submit" className="fx-btn fx-btn--primary fx-btn--lg">
        <Mail size={18} aria-hidden /> Email my request
      </button>
      <p className="fx-form__note" aria-live="polite">
        {sent
          ? `Your email app should now be open with your request filled in — press send and we will reply, usually within one business day. If nothing opened, email us at ${COMPANY_EMAIL}.`
          : `Submitting opens your email app with a message to ${COMPANY_EMAIL}. Requesting a quote is free and does not commit you to booking.`}
      </p>
    </form>
  );
}

export default function HomePage() {
  usePageMeta(DEFAULT_PAGE_META);

  return (
    <SiteLayout>
      <section className="fx-hero" style={{ backgroundImage: `url(${img('hero.jpg')})` }}>
        <div className="fx-wrap fx-hero__inner">
          <p className="fx-kicker fx-kicker--light">Independent travel agency</p>
          <h1>Search flights, hotels, cars, cruises and packages</h1>
          <p className="fx-hero__lead">
            Compare options for your next trip, then let a {SITE_NAME} travel advisor confirm the best price and book it
            for you — with every fare rule and fee shown before you pay.
          </p>
          <HeroSearch />
        </div>
      </section>

      <section id="services" className="fx-section">
        <div className="fx-wrap">
          <header className="fx-head">
            <p className="fx-kicker">What we help with</p>
            <h2>One agency for the whole trip</h2>
            <p>Bring us a single flight or a two-week itinerary — the same advisor can look after all of it.</p>
          </header>
          <div className="fx-cards">
            {SERVICES.map(({ Icon, title, text }) => (
              <article key={title} className="fx-card">
                <span className="fx-card__icon">
                  <Icon size={22} aria-hidden />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="fx-section fx-section--soft">
        <div className="fx-wrap">
          <header className="fx-head">
            <p className="fx-kicker">How it works</p>
            <h2>Three simple steps, no surprises</h2>
          </header>
          <ol className="fx-steps">
            {STEPS.map(({ Icon, title, text }, i) => (
              <li key={title} className="fx-step">
                <span className="fx-step__num">{i + 1}</span>
                <Icon size={26} aria-hidden className="fx-step__icon" />
                <h3>{title}</h3>
                <p>{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="fx-section">
        <div className="fx-wrap fx-split">
          <img src={img('travel-planning.jpg')} alt="Traveller planning a trip with a map" loading="lazy" />
          <div>
            <p className="fx-kicker">Our commitments</p>
            <h2>Straightforward about who we are and what you pay</h2>
            <ul className="fx-checks">
              {PROMISES.map((p) => (
                <li key={p}>
                  <CheckCircle2 size={20} aria-hidden /> {p}
                </li>
              ))}
            </ul>
            <Link to="/about-us" className="fx-btn fx-btn--outline">
              More about {SITE_NAME}
            </Link>
          </div>
        </div>
      </section>

      <section id="destinations" className="fx-section fx-section--soft">
        <div className="fx-wrap">
          <header className="fx-head">
            <p className="fx-kicker">Inspiration</p>
            <h2>Places our travellers love</h2>
            <p>Not sure where to go yet? Here are a few favourites to get you started.</p>
          </header>
          <div className="fx-dests">
            {DESTINATIONS.map((d) => (
              <article key={d.city} className="fx-dest">
                <img src={img(d.image)} alt={`${d.city}, ${d.country}`} loading="lazy" />
                <div className="fx-dest__body">
                  <h3>
                    {d.city} <small>{d.country}</small>
                  </h3>
                  <p>{d.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="quote" className="fx-section">
        <div className="fx-wrap fx-quote">
          <div className="fx-quote__intro">
            <p className="fx-kicker">Free, no obligation</p>
            <h2>Request a trip quote</h2>
            <p>
              Share a few details and a {SITE_NAME} advisor will reply with options and an itemised price. Prefer to
              talk? Call us — {SUPPORT_HOURS.toLowerCase()}.
            </p>
            <a href={`tel:${MAIN_PHONE_TEL}`} className="fx-quote__phone">
              <Phone size={20} aria-hidden /> {MAIN_PHONE_DISPLAY}
            </a>
            <p className="fx-small">{CALL_NOTE}</p>
          </div>
          <QuoteForm />
        </div>
      </section>

      <section id="faq" className="fx-section fx-section--soft">
        <div className="fx-wrap fx-faq">
          <header className="fx-head">
            <p className="fx-kicker">FAQ</p>
            <h2>Questions travellers ask us</h2>
          </header>
          {FAQS.map((f) => (
            <details key={f.q} className="fx-faq__item">
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}
